/**
 * Who may do what on the handheld ingest screens (build plan section 7,
 * Segment 5; decided by Ivin 2026-10-07): whoever runs the capture session
 * ingests.
 *   - admin and rig_operator: add, rename, category, reference photo, raw,
 *     projects, exports, Verify;
 *   - erase (an empty subject, Combine, a rollback's leftovers): admin only,
 *     because erasing uses up the number;
 *   - everyone else views.
 * The callables decide (functions/src/services/handheldRoles.ts); this only
 * hides what a person cannot use. It reads the same thing the callables read:
 * the sign-in token's custom claims (role, assignedJobs), never the Suite
 * profile's role (a different list).
 */
import { useEffect, useState } from "react";
import { auth } from "../firebase.js";

export const VIEW_ONLY_MESSAGE = "View only. Rig operators and admins add and change subjects.";
export const ERASE_ADMIN_ONLY = "Only an admin can erase (it uses up the number).";

/**
 * What the claims allow on one job. A rig_operator whose claims list jobs
 * writes only on those (the callables' requireJobAccess); an admin anywhere.
 */
export function handheldAccessFor(claims, jobId) {
  const role = typeof claims?.role === "string" ? claims.role : null;
  const raw = claims?.assignedJobs;
  // As crewAuth.requireAuth: a rig_operator's job list that is not a list of
  // job ids only is refused (never read as "every job").
  const malformed = raw != null && (!Array.isArray(raw) || raw.some((j) => typeof j !== "string"));
  const list = Array.isArray(raw) ? raw : [];
  const scopedOk = !malformed && (list.length === 0 || (Boolean(jobId) && list.includes(jobId)));
  const canWrite = role === "admin" || (role === "rig_operator" && scopedOk);
  const canErase = role === "admin";
  return { role, canWrite, canErase };
}

/**
 * Follows the signed-in account's token: each change (sign-in, sign-out,
 * another account, a refresh) clears what is allowed at once ({claims: null,
 * loading: true}), then reads the new token; only the read of the LATEST
 * change is applied, so a slow read from before a sign-out or an account
 * switch can never bring back the old account's claims. A read that fails
 * allows nothing. Returns { onUser(user), stop() }.
 */
export function createClaimsTracker(setState) {
  let seq = 0;
  let stopped = false;
  const apply = (n, next) => { if (!stopped && n === seq) setState(next); };
  return {
    onUser(user) {
      const n = ++seq;
      if (!user) { apply(n, { claims: null, loading: false }); return Promise.resolve(); }
      apply(n, { claims: null, loading: true });
      return Promise.resolve()
        .then(() => user.getIdTokenResult())
        .then((token) => apply(n, { claims: token?.claims ?? null, loading: false }))
        .catch(() => apply(n, { claims: null, loading: false }));
    },
    stop() { stopped = true; },
  };
}

/**
 * The signed-in account's claims: { claims, loading }. Until they are read,
 * and if they cannot be read, nothing is allowed (the screen shows no
 * controls); the callables refuse anyway.
 */
export function useCrewClaims(source = auth) {
  const [state, setState] = useState({ claims: null, loading: true });
  useEffect(() => {
    const tracker = createClaimsTracker(setState);
    let unsubscribe = null;
    if (typeof source?.onIdTokenChanged === "function") {
      unsubscribe = source.onIdTokenChanged((u) => { tracker.onUser(u); });
    } else {
      tracker.onUser(source?.currentUser ?? null);
    }
    return () => { tracker.stop(); if (typeof unsubscribe === "function") unsubscribe(); };
  }, [source]);
  return state;
}
